import { cn } from "@/lib/cn";
import type { AnchorHTMLAttributes, ButtonHTMLAttributes } from "react";

type Variant = "primary" | "outline" | "outline-brand" | "ghost";
type Size = "default" | "lg";

const VARIANTS: Record<Variant, string> = {
  primary: "bg-brand text-inverse hover:bg-brand-hover",
  outline: "border border-primary bg-primary text-primary hover:bg-tertiary",
  "outline-brand": "border border-brand text-brand hover:bg-brand-subtle",
  ghost: "text-secondary hover:text-primary",
};

const SIZES: Record<Size, string> = {
  default: "h-9 px-4 text-sm",
  lg: "px-10 py-4 text-sm",
};

export function buttonClass(
  variant: Variant = "primary",
  size: Size = "default",
) {
  return cn(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40",
    VARIANTS[variant],
    SIZES[size],
  );
}

type Props = AnchorHTMLAttributes<HTMLAnchorElement> & {
  variant?: Variant;
  size?: Size;
};

export function ButtonLink({ variant, size, className, ...props }: Props) {
  return <a className={cn(buttonClass(variant, size), className)} {...props} />;
}

export function Button({
  variant,
  size,
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
}) {
  return (
    <button
      className={cn(
        buttonClass(variant, size),
        "disabled:pointer-events-none disabled:opacity-60",
        className,
      )}
      {...props}
    />
  );
}
